import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import popup from "./popup";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import {
  isGameOver,
  isLevelScoreRecorded,
  saveGameStageAndReport,
} from "../../functions/stageReportSync";
import {
  GAME_BY_KEY,
  PLANS_URL,
  THANKYOU_ROUTE,
  TOTAL_GAMES,
  WELCOME_ROUTE,
} from "./gameConfig";
import {
  formatSecondsToClock,
  getBackendBase,
  getStoredUser,
  isDemoUser,
  loadPlayerStage,
  playedLevels,
  sumGameScores,
} from "./gameUtils";

/**
 * The page unload request is not guaranteed to finish during a refresh, so the clock is also
 * mirrored per tab; on load it only wins when it is ahead of what the DB has (same player + game).
 */
const CLOCK_MIRROR_KEY = "desi_game_clock";
/** Seconds between clock-only syncs to the DB (state changes are saved sooner, see saveProgress). */
const PROGRESS_HEARTBEAT_SEC = 30;
/** Quiet time after the last state change before it is saved (batches typing / rapid clicks). */
const PROGRESS_DEBOUNCE_MS = 800;

function readClockMirror(stageId, level) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(CLOCK_MIRROR_KEY) || "null");
    if (saved && String(saved.stageId) === String(stageId) && saved.level === level) {
      return Number(saved.sec) || 0;
    }
  } catch {
    /* ignore */
  }
  return 0;
}

function writeClockMirror(stageId, level, sec) {
  try {
    sessionStorage.setItem(CLOCK_MIRROR_KEY, JSON.stringify({ stageId, level, sec }));
  } catch {
    /* ignore */
  }
}

/**
 * Shared plumbing of the old per-game pages: the running clock (continues from
 * stages.time), the score box, the Skip button and the submit-and-return step
 * of submit_unique.php / update_pos.php.
 */
export default function useDesiGame(gameKey) {
  const game = GAME_BY_KEY[gameKey];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);

  const storedUser = useMemo(() => user || getStoredUser(), [user]);
  const isDemoBypass = useMemo(() => isDemoUser(storedUser), [storedUser]);
  const backendBase = getBackendBase();

  const [ready, setReady] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [score, setScore] = useState(0);
  const [saving, setSaving] = useState(false);
  /** In-progress state of this game from the DB (null when starting fresh). */
  const [restored, setRestored] = useState(null);

  const stageIdRef = useRef("");
  const stateRef = useRef(undefined);
  const scoreRef = useRef(0);
  const doneRef = useRef(false);
  const saveTimerRef = useRef(0);
  const stageRef = useRef(null);
  const elapsedRef = useRef(0);
  const sentRef = useRef(false);

  useEffect(() => {
    dispatch(setBackButtonUrl(WELCOME_ROUTE));
  }, [dispatch]);

  useEffect(() => {
    if (!game) {
      navigate(WELCOME_ROUTE, { replace: true });
      return undefined;
    }
    if (!storedUser?.userId && !storedUser?.id) {
      navigate("/", { replace: true });
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const loaded = await loadPlayerStage({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
      });
      if (cancelled) return;

      const { stage } = loaded;
      if (stage && isGameOver(stage)) {
        navigate(THANKYOU_ROUTE, { replace: true });
        return;
      }
      // Each state can be played once (welcome.php: "already been explored").
      if (stage && isLevelScoreRecorded(stage, game.level)) {
        navigate(WELCOME_ROUTE, { replace: true });
        return;
      }

      stageIdRef.current = loaded.stageId;
      stageRef.current = stage;
      const startSec = Math.max(
        loaded.elapsedSec,
        readClockMirror(loaded.stageId, game.level)
      );
      elapsedRef.current = startSec;
      setElapsed(startSec);

      // Refresh: pick up this game's points and state exactly where the player left off.
      const progress = stage?.progress?.[game.level];
      if (progress) {
        stateRef.current = progress.state;
        scoreRef.current = Number(progress.score) || 0;
        setScore(scoreRef.current);
        setRestored(progress.state ?? null);
      }
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameKey]);

  useEffect(() => {
    if (!ready) return undefined;
    const id = window.setInterval(() => {
      elapsedRef.current += 1;
      setElapsed(elapsedRef.current);
      writeClockMirror(stageIdRef.current, game.level, elapsedRef.current);
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  /**
   * Progress sync (gameProgressSave). Kept deliberately light, because every player runs it:
   *  - state changes (found word, typed answer, attempt) are batched: one save ~0.8 s after the last change
   *  - the clock alone is synced every 30 s; a refresh in between continues from the per-tab clock mirror
   *  - nothing is sent when state, score and clock are unchanged; one request in flight at a time
   *  - a last save goes out when the tab is hidden / closed / left
   */
  const ctxRef = useRef({});
  ctxRef.current = { game, isDemoBypass, backendBase, adminToken, storedUser };
  const lastSentRef = useRef({ sig: "", sec: -1 });
  const inFlightRef = useRef(false);
  const dirtyRef = useRef(false);

  const flushProgress = useCallback(function flush(keepalive = false) {
    const { game: g, isDemoBypass: demo, backendBase: base, adminToken: token, storedUser: u } = ctxRef.current;
    if (!g || demo || doneRef.current || !base || !token || !stageIdRef.current) return;

    const state = stateRef.current;
    const sig = JSON.stringify([state === undefined ? null : state, scoreRef.current]);
    const sec = elapsedRef.current;
    const last = lastSentRef.current;
    const changed = sig !== last.sig;
    // Unchanged state + score and the clock has barely moved: nothing worth a request.
    if (!changed && sec - last.sec < (keepalive ? 2 : PROGRESS_HEARTBEAT_SEC - 1)) return;
    if (inFlightRef.current && !keepalive) {
      dirtyRef.current = true;
      return;
    }

    const body = {
      userId: u.userId || u.userid || u.id,
      sessionId: u.sessionId,
      organizationId: u.organizationId,
      stageId: stageIdRef.current,
      stageNumber: g.level,
      email: u.email || "",
      score: scoreRef.current,
      time: formatSecondsToClock(sec),
    };
    if (state !== undefined) body.state = state;

    // Leaving the page: sendBeacon is the one request the browser still delivers while the tab closes.
    // It cannot carry an Authorization header, so the token rides in the (text/plain) body.
    if (keepalive && typeof navigator !== "undefined" && navigator.sendBeacon) {
      try {
        const queued = navigator.sendBeacon(
          `${base}/gameProgressSave`,
          new Blob([JSON.stringify({ ...body, authToken: token })], { type: "text/plain" })
        );
        if (queued) {
          lastSentRef.current = { sig, sec };
          return;
        }
      } catch {
        /* fall through to fetch */
      }
    }

    inFlightRef.current = true;
    lastSentRef.current = { sig, sec };
    fetch(`${base}/gameProgressSave`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      keepalive,
    })
      .then((res) => {
        if (!res.ok) lastSentRef.current = last; // try again on the next beat
      })
      .catch(() => {
        lastSentRef.current = last;
      })
      .finally(() => {
        inFlightRef.current = false;
        if (dirtyRef.current && !doneRef.current) {
          dirtyRef.current = false;
          flush();
        }
      });
  }, []);

  /** Games call this whenever their state changes (found words, typed answers, attempts…). */
  const saveProgress = useCallback(
    (state) => {
      stateRef.current = state;
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = window.setTimeout(() => flushProgress(), PROGRESS_DEBOUNCE_MS);
    },
    [flushProgress]
  );

  useEffect(() => {
    if (!ready) return undefined;
    // Start from what the DB already has, so loading a game does not immediately re-send it.
    lastSentRef.current = {
      sig: JSON.stringify([stateRef.current === undefined ? null : stateRef.current, scoreRef.current]),
      sec: elapsedRef.current,
    };
    const beat = window.setInterval(() => {
      if (!document.hidden) flushProgress();
    }, PROGRESS_HEARTBEAT_SEC * 1000);
    const onHide = () => flushProgress(true);
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushProgress(true);
    };
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(beat);
      window.clearTimeout(saveTimerRef.current);
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener("visibilitychange", onVisibility);
      flushProgress(true);
    };
  }, [ready, flushProgress]);

  /** Store this game's points + time, then go back to the pegs (or the end). */
  const submit = useCallback(
    async (points, answers = null) => {
      if (sentRef.current || !game) return;
      sentRef.current = true;
      doneRef.current = true; // the finished game replaces its in-progress state
      window.clearTimeout(saveTimerRef.current);
      setSaving(true);

      const time = formatSecondsToClock(elapsedRef.current);

      if (isDemoBypass) {
        await popup.fire({
          icon: "success",
          title: "Great start!",
          text: "Subscribe to any PLAN to play with your peers.",
          confirmButtonText: "View plans",
        });
        window.location.assign(PLANS_URL);
        return;
      }

      const stage = stageRef.current;
      const playedAfter = new Set([...playedLevels(stage), game.level]).size;
      const finished = playedAfter >= TOTAL_GAMES;
      const total = sumGameScores(stage, game.level) + points;

      const result = await saveGameStageAndReport({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId: stageIdRef.current,
        stageNumber: game.level,
        score: points,
        answers,
        time,
        total_score: total,
        gameover: finished,
      });

      if (!result.ok) {
        sentRef.current = false;
        doneRef.current = false;
        setSaving(false);
        popup.fire({
          icon: "error",
          title: "Something went wrong",
          text: "We could not save your score. Please try again.",
          confirmButtonText: "Retry",
        });
        return;
      }
      navigate(finished ? THANKYOU_ROUTE : WELCOME_ROUTE, { replace: true });
    },
    [game, isDemoBypass, backendBase, adminToken, storedUser, navigate]
  );

  /** A game registers what Skip should show first (its right answers); null = nothing. */
  const revealRef = useRef(null);
  const skipBusyRef = useRef(false);
  const setReveal = useCallback((fn) => {
    revealRef.current = fn;
  }, []);

  const skip = useCallback(async () => {
    if (skipBusyRef.current || sentRef.current) return;
    skipBusyRef.current = true;
    try {
      if (revealRef.current) await revealRef.current();
    } finally {
      skipBusyRef.current = false;
    }
    submit(0, null);
  }, [submit]);

  return {
    game,
    ready,
    saving,
    score,
    setScore,
    initialState: restored,
    setReveal,
    saveProgress,
    timeLabel: formatSecondsToClock(elapsed),
    submit,
    skip,
  };
}
